import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import LoadingButton from '../LoadingButton';
import partnerApiService from '../../services/partnerApi';
import { formatCredits } from '../../utils/credits';

// COMPRAR créditos A GRANEL. El partner compra ÉL MISMO, a su PRECIO MAYORISTA
// PLANO (el mismo por crédito compre 100 o 100.000): no revende paquetes con
// margen. Lo único que le acota es su LÍMITE DE COMPRA MENSUAL —cuántos créditos
// puede comprar por ciclo—, y cuando se le queda pequeño lo que pide es
// AMPLIARLO, no créditos sueltos.
//
// Tres cosas que el diálogo tiene que dejar claras antes de confirmar, porque
// es donde se confunden créditos y dinero:
//   · CRÉDITOS es lo que compras (la unidad de la bolsa);
//   · IMPORTE es lo que se te facturará en USD a tu precio plano — se calcula en
//     vivo aquí con las mismas cifras que el statement;
//   · el LÍMITE es MENSUAL: se compara contra lo COMPRADO este mes, no contra tu
//     saldo (asignar a clientes baja el saldo pero no libera cupo de compra);
//   · sin pasarela: esto no cobra hoy, entra en la factura del mes.

interface Props {
  open: boolean;
  onClose: () => void;
  onPurchased?: () => void;
  // Preselección desde la referencia del catálogo (comprar tantos créditos como
  // trae un paquete). Es un atajo, no una reventa del paquete.
  presetCredits?: number | string | null;
  presetPackageId?: number | null;
  presetPackageName?: string | null;
  // Contexto para calcular el importe y el hueco del cupo MENSUAL. Vienen de
  // /partner/me: su descuento, el límite de compra mensual (0 = sin límite) y lo
  // ya COMPRADO en el ciclo vigente (el «usado» del cupo, NO el saldo) + cuándo
  // se renueva.
  maxWholesaleCredits?: number | string | null;
  purchasedThisCycle?: number | string | null;
  cycleRenewsAt?: string | null;
  discountPct?: number | string | null;
  creditPriceUsd?: number | string | null;
  // Abrir el diálogo de ampliación de límite cuando la compra no cabe: es la
  // única salida, y ofrecerla aquí evita que el partner tenga que buscarla.
  onRequestLimit?: () => void;
}

const PurchaseCreditsDialog: React.FC<Props> = ({
  open,
  onClose,
  onPurchased,
  presetCredits,
  presetPackageId,
  presetPackageName,
  maxWholesaleCredits,
  purchasedThisCycle,
  cycleRenewsAt,
  discountPct,
  creditPriceUsd,
  onRequestLimit
}) => {
  const { t, i18n } = useTranslation();
  const [credits, setCredits] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Al abrir se rearma con el preset del paquete (si viene): el diálogo es
  // compartido y arrastrar el estado anterior haría comprar la cifra
  // equivocada.
  useEffect(() => {
    if (!open) return;
    setCredits(
      presetCredits !== null && presetCredits !== undefined ? String(Number(presetCredits)) : ''
    );
    setError('');
  }, [open, presetCredits]);

  const amount = Number(credits);
  const valid = credits.trim() !== '' && !Number.isNaN(amount) && amount > 0;

  const cap = Number(maxWholesaleCredits ?? 0);
  // 0 = SIN límite mensual (semántica del backend), no «cero crédito».
  const hasCap = cap > 0;
  // El cupo se mide contra lo COMPRADO este ciclo, no contra el saldo.
  const purchased = Number(purchasedThisCycle ?? 0);
  const headroom = hasCap ? Math.max(0, cap - purchased) : null;
  const overCap = hasCap && valid && purchased + amount > cap;
  const renewsLabel = cycleRenewsAt
    ? new Date(cycleRenewsAt).toLocaleDateString(i18n.language || 'es')
    : null;

  // IMPORTE en vivo, con la MISMA aritmética que el backend y el statement:
  // créditos × precio de lista × (1 − descuento) = su precio mayorista plano.
  // Es una estimación de UI (el backend manda y devuelve el importe real), pero
  // tiene que coincidir — por eso ambas cifras vienen del servidor y aquí no se
  // hardcodea ninguna.
  const price = Number(creditPriceUsd ?? 0);
  const pct = Number(discountPct ?? 0);
  const estimatedUsd = valid && price > 0 ? amount * price * (1 - pct / 100) : null;

  const handleSubmit = async () => {
    if (!valid || overCap) return;
    try {
      setLoading(true);
      setError('');
      const res = await partnerApiService.purchaseCredits({
        credits: amount,
        credit_package_id: presetPackageId ?? undefined
      });
      toast.success(
        t('partner.purchase.done', 'Compra registrada: {{credits}} créditos · {{usd}} USD a facturar', {
          credits: formatCredits(res.credits),
          usd: Number(res.amount_usd ?? 0).toFixed(2)
        })
      );
      onClose();
      onPurchased?.();
    } catch (err: any) {
      console.error('Failed to purchase credits:', err);
      // 409 = se pasa del techo. El detail del backend ya dice cuánto cabe.
      setError(err?.detail || t('partner.purchase.error', 'No se pudo completar la compra'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('partner.purchase.title', 'Comprar créditos')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {/* El límite, explicado como lo que es: un cupo de compra MENSUAL. */}
        <Alert severity="info" sx={{ mb: 2 }}>
          {hasCap
            ? t('partner.purchase.capHint', 'Compras cuando quieras a tu precio mayorista. Tu límite de compra este mes es de {{cap}} créditos y ya has comprado {{purchased}}: te quedan {{headroom}} este mes{{renews}}.', {
                cap: formatCredits(cap),
                purchased: formatCredits(purchased),
                headroom: formatCredits(headroom ?? 0),
                renews: renewsLabel ? ` (${t('partner.purchase.renewsInline', 'se renueva el {{date}}', { date: renewsLabel })})` : ''
              })
            : t('partner.purchase.noCapHint', 'No tienes límite de compra mensual: compra los créditos que necesites.')}
        </Alert>
        {presetPackageName && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('partner.purchase.fromPackageRef', 'Referencia: tantos créditos como el paquete «{{name}}» (los compras a granel a tu precio, no revendes el paquete).', { name: presetPackageName })}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          type="number"
          fullWidth
          variant="outlined"
          label={t('partner.purchase.credits', 'Créditos que compras') + ' *'}
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
          inputProps={{ min: 1, step: '1' }}
          error={(credits !== '' && !valid) || overCap}
          helperText={
            credits !== '' && !valid
              ? t('partner.purchase.creditsPositive', 'Compra más de cero créditos')
              : undefined
          }
          sx={{ mb: 2 }}
        />

        {/* CRÉDITOS ↔ IMPORTE, separados y con sus unidades: es exactamente la
            confusión que hay que evitar. */}
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t('partner.purchase.creditsLabel', 'Créditos que entran en tu bolsa')}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {valid ? formatCredits(amount) : '—'}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="baseline">
          <Typography variant="body2" color="text.secondary">
            {t('partner.purchase.amountLabel', 'Importe a facturar')}
            {pct > 0
              ? ` · ${t('partner.purchase.withDiscount', 'con tu −{{pct}} %', { pct })}`
              : ''}
          </Typography>
          <Typography variant="h6" fontWeight={700} color="primary.main">
            {estimatedUsd === null ? '—' : `${estimatedUsd.toFixed(2)} USD`}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('partner.purchase.noGateway', 'No se cobra ahora: esta compra entra en tu factura mensual.')}
        </Typography>

        {/* No cabe en su cupo mensual: se frena aquí con el motivo y con la
            ÚNICA salida real, que es pedir ampliación (no puede subírselo él). */}
        {overCap && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('partner.purchase.overCap', 'Este mes ya has comprado {{purchased}} de tus {{cap}} créditos de límite mensual. Con esta compra llegarías a {{total}}. Puedes comprar {{headroom}} más este mes{{renews}}, o pedir ampliar tu límite de compra mensual.', {
              purchased: formatCredits(purchased),
              total: formatCredits(purchased + amount),
              cap: formatCredits(cap),
              headroom: formatCredits(headroom ?? 0),
              renews: renewsLabel ? ` (${t('partner.purchase.renewsInline', 'se renueva el {{date}}', { date: renewsLabel })})` : ''
            })}
            {onRequestLimit && (
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    onClose();
                    onRequestLimit();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {t('partner.limitRequest.title', 'Solicitar ampliación de límite mensual')}
                </Button>
              </Box>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel', 'Cancelar')}
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          disabled={!valid || overCap}
          variant="contained"
        >
          {t('partner.purchase.submit', 'Comprar')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseCreditsDialog;
